import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '../../stores/auth.store';
import { useUrlState } from '../../hooks/useUrlState';
import { Icon } from '../../components/ui/Icon';
import { Breadcrumb } from '../../components/ui/Breadcrumb';
import { Chip } from '../../components/ui/Chip';
import { DetailSkeleton } from '../../components/ui/Skeleton';
import { DesktopOnly } from '../../components/ui/DesktopOnly';
import { useIsMobile } from '../../hooks/useIsMobile';
import {
  PipelineKind, PIPELINE_KIND_HINT, PIPELINE_KIND_LABEL,
  StageOutcome, STAGE_OUTCOME_LABEL, VOUCHER_REQUISITI_STAGE,
} from '@masamune/shared';
import { canWriteDeals } from './dealsApi';
import {
  pipelinesConfigQuery, usePipelineActions, mancanzeStep,
  type MoveDirection, type PipelineConfig, type PipelineStageConfig,
} from './pipelinesApi';
import './deals.css';

/**
 * Pipeline — i processi commerciali e i loro step, configurabili senza toccare
 * il codice.
 *
 * Perché sta in Trattative e non in Impostazioni. Due ragioni, tutte e due
 * pratiche. La prima è il permesso: qui si scrive con il modulo TRATTATIVE, e
 * infilare la schermata dentro Impostazioni avrebbe voluto dire far passare da
 * quella porta chi deve solo sistemare i propri step, aprendogli accanto utenti,
 * ruoli e integrazioni. La seconda è il momento: il bisogno nasce sempre mentre
 * si guardano le trattative e ci si accorge che un passaggio manca, quindi ci si
 * arriva da lì con un clic, dal bottone accanto alla tendina delle pipeline.
 *
 * Disegno: quello del dettaglio trattativa. Righe nude separate da hairline,
 * ogni campo salva da solo appena si esce dal campo (PATCH parziale + toast),
 * il riordino è con due frecce.
 *
 * Due cose sono di sola lettura, e la schermata lo dice: il nome interno (lo
 * usano i link e la preferenza salvata nel browser, cambiarlo li romperebbe) e
 * i numeri d'ordine, che si spostano ma non si digitano.
 */
/**
 * Gli stessi tetti che ha lo schema Zod (packages/shared/src/schemas/pipeline.ts).
 * Stanno anche qui perché il campo si fermi da solo: se il limite lo facesse
 * rispettare solo il server, chi scrive un nome lungo si prenderebbe un errore
 * di validazione in inglese quando esce dal campo, senza capire cosa ha sbagliato.
 */
const MAX_NOME = 80;
const MAX_DESCRIZIONE = 300;

export function PipelinesPage() {
  const user = useAuthStore((s) => s.user);
  const canWrite = canWriteDeals(user);
  const mobile = useIsMobile();
  const canConfigure = canWrite && !mobile;
  const { data: pipelines, isLoading } = useQuery(pipelinesConfigQuery);
  const [slug, setSlug] = useUrlState<string>('p', '');
  const [creating, setCreating] = useState(false);
  const act = usePipelineActions();

  if (isLoading || !pipelines) return <DetailSkeleton header="chips" kpis={0} cards={1} />;

  // Senza una scelta esplicita si apre la prima: la pagina non è mai vuota
  const selected = pipelines.find((p) => p.slug === slug) ?? pipelines[0];

  // Eliminata quella aperta, la selezione torna sulla prima: senza, la pagina
  // resterebbe a puntare a qualcosa che non c'è più. Il rifiuto (trattative
  // dentro) lo racconta già il toast dell'azione, qui basta non esplodere.
  const onDeletePipeline = async (p: PipelineConfig) => {
    try {
      const done = await act.deletePipeline.mutateAsync({ id: p.id, name: p.name });
      if (done) setSlug('');
    } catch { /* il messaggio del server è già a schermo */ }
  };

  return (
    <div className="page">
      <Breadcrumb items={[{ label: 'Trattative', to: '/trattative' }, { label: 'Fasi delle trattative' }]} />

      <div className="page-ed-hd flex items-end justify-between gap-4">
        <div className="min-w-0 max-w-[760px]">
          <h1 className="ed-title">Fasi delle trattative</h1>
          <div className="ed-sub">Le pipeline e le loro fasi: quello che cambi qui diventa le colonne della bacheca.</div>
        </div>
        <div className="flex gap-2 pb-1 shrink-0">
          {canWrite && !creating && (
            <DesktopOnly
              cosa="La configurazione delle pipeline"
              perche="creazione, riordino e modifica degli step richiedono il computer; qui puoi leggere processi, passaggi e utilizzo."
            >
              <button className="btn btn-primary" onClick={() => setCreating(true)}>
                <Icon name="plus" /> Nuova pipeline
              </button>
            </DesktopOnly>
          )}
        </div>
      </div>

      <div className="pl-detail">
        <div className="data-hd">
          <span className="tk-sez-t">Le pipeline</span>
          <span className="mute text-[12px]">{pipelines.length} in tutto</span>
        </div>

        <div className="flex flex-col">
          {pipelines.map((p, i) => (
            <div key={p.id} className="flex items-center gap-1">
              <button
                type="button"
                className="pl-row"
                aria-selected={p.id === selected?.id}
                onClick={() => setSlug(p.slug)}
              >
                <div className="min-w-0">
                  <div className="pl-row-name truncate">
                    {p.name}
                    {!p.active && <span className="mute-2 font-normal"> · spenta</span>}
                  </div>
                  <div className="pl-row-sub truncate">
                    {PIPELINE_KIND_LABEL[p.kind]} · {p.stages.length} step ·{' '}
                    {p.dealsCount === 0 ? 'nessuna trattativa' : `${p.dealsCount} trattativ${p.dealsCount === 1 ? 'a' : 'e'}`}
                  </div>
                </div>
                <div className="pl-row-side">
                  {p.kind === PipelineKind.VOUCHER && <Chip tone="info">Bando</Chip>}
                  <Icon name="chevronR" size={14} className="mute-2" />
                </div>
              </button>
              {canConfigure && (
                <Riordino
                  primo={i === 0}
                  ultimo={i === pipelines.length - 1}
                  pending={act.movePipeline.isPending}
                  cosa="la pipeline"
                  onMove={(direction) => act.movePipeline.mutate({ id: p.id, direction })}
                />
              )}
            </div>
          ))}
        </div>

        {canConfigure && creating && <NuovaPipeline onClose={() => setCreating(false)} onCreated={(s) => { setCreating(false); setSlug(s); }} />}

        {selected && (
          <>
            <div className="ed-divider" />
            <DettaglioPipeline
              key={selected.id}
              p={selected}
              canWrite={canConfigure}
              onDelete={() => onDeletePipeline(selected)}
            />
          </>
        )}
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// Nuova pipeline: nome, a cosa serve, tipo di processo
// ════════════════════════════════════════════════════════════════════

function NuovaPipeline({ onClose, onCreated }: { onClose: () => void; onCreated: (slug: string) => void }) {
  const { createPipeline } = usePipelineActions();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [kind, setKind] = useState<PipelineKind>(PipelineKind.ALTRO);

  const salva = async () => {
    const n = name.trim();
    if (!n) return;
    try {
      const p = await createPipeline.mutateAsync({ name: n, description: description.trim() || null, kind });
      onCreated(p.slug);
    } catch { /* il motivo lo dice già il toast: il form resta com'è, niente si perde */ }
  };

  return (
    <div className="mt-5">
      <div className="data-hd"><span className="tk-sez-t">Nuova pipeline</span></div>
      <div className="ed-kv">
        <Riga label="Nome" hint="come la chiamate voi: compare nelle tendine">
          <input
            className="inp tk-inline"
            autoFocus
            maxLength={MAX_NOME}
            placeholder="Per esempio: Bandi regionali"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') void salva(); if (e.key === 'Escape') onClose(); }}
          />
        </Riga>
        <Riga label="A cosa serve" hint="una riga, facoltativa">
          <input
            className="inp tk-inline"
            maxLength={MAX_DESCRIZIONE}
            placeholder="Di che trattative si occupa"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </Riga>
        <Riga label="Tipo di processo" hint={PIPELINE_KIND_HINT[kind]}>
          <select className="inp tk-inline" value={kind} onChange={(e) => setKind(e.target.value as PipelineKind)}>
            {Object.values(PipelineKind).map((k) => <option key={k} value={k}>{PIPELINE_KIND_LABEL[k]}</option>)}
          </select>
        </Riga>
      </div>
      <div className="adm-note mt-3">
        <Icon name="help" size={12} className="mute" />
        <span>
          Nasce con quattro passaggi di partenza (da contattare, in trattativa, vinta, persa): li sistemi
          subito dopo, qui sotto.
        </span>
      </div>
      <div className="flex gap-2">
        <button className="btn btn-primary btn-sm" disabled={!name.trim() || createPipeline.isPending} onClick={() => void salva()}>
          <Icon name="check" size={12} /> Crea
        </button>
        <button className="btn btn-sm btn-ghost" onClick={onClose}>Annulla</button>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// Dettaglio: i campi della pipeline e i suoi step
// ════════════════════════════════════════════════════════════════════

function DettaglioPipeline({ p, canWrite, onDelete }: { p: PipelineConfig; canWrite: boolean; onDelete: () => void }) {
  const { updatePipeline } = usePipelineActions();
  const patch = (data: Record<string, unknown>) => updatePipeline.mutate({ id: p.id, ...data });

  return (
    <>
      <div className="data-hd">
        <span className="tk-sez-t">{p.name}</span>
        {updatePipeline.isPending && <span className="mute text-[12px]">Salvo…</span>}
      </div>

      <div className="ed-kv">
        <Riga label="Nome">
          {canWrite ? (
            <input
              className="inp tk-inline"
              key={p.name}
              maxLength={MAX_NOME}
              defaultValue={p.name}
              onBlur={(e) => { const v = e.target.value.trim(); if (v && v !== p.name) patch({ name: v }); }}
            />
          ) : p.name}
        </Riga>

        <Riga label="A cosa serve">
          {canWrite ? (
            <input
              className="inp tk-inline"
              key={p.description ?? ''}
              maxLength={MAX_DESCRIZIONE}
              defaultValue={p.description ?? ''}
              placeholder="Di che trattative si occupa"
              onBlur={(e) => { const v = e.target.value.trim(); if (v !== (p.description ?? '')) patch({ description: v || null }); }}
            />
          ) : (p.description || '—')}
        </Riga>

        <Riga label="Tipo di processo">
          {canWrite ? (
            <select className="inp tk-inline" value={p.kind} onChange={(e) => patch({ kind: e.target.value })}>
              {Object.values(PipelineKind).map((k) => <option key={k} value={k}>{PIPELINE_KIND_LABEL[k]}</option>)}
            </select>
          ) : PIPELINE_KIND_LABEL[p.kind]}
        </Riga>

        <Riga label="In uso" hint="spenta sparisce dalle scelte, le trattative restano">
          {canWrite ? (
            <div className="seg-control tk-seg-sm">
              <button aria-pressed={p.active} onClick={() => { if (!p.active) patch({ active: true }); }}>Accesa</button>
              <button aria-pressed={!p.active} onClick={() => { if (p.active) patch({ active: false }); }}>Spenta</button>
            </div>
          ) : (p.active ? 'Accesa' : 'Spenta')}
        </Riga>

        {/* Nascosti il 26/10/2026 (pagina alleggerita): il nome interno è un
            dato tecnico, e le trattative dentro sono già scritte nella riga
            della pipeline qui sopra. Per rimetterli basta togliere `false &&`. */}
        {false && (
          <>
            <Riga label="Nome interno">
              <span className="mono mute">{p.slug}</span>
            </Riga>
            <Riga label="Trattative dentro">
              {p.dealsCount === 0 ? <span className="mute">nessuna</span> : `${p.dealsCount}`}
            </Riga>
          </>
        )}
      </div>

      <StepDellaPipeline p={p} canWrite={canWrite} />

      {canWrite && (
        <>
          <div className="ed-divider" />
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <span className="mute text-[12px]">
              {p.dealsCount > 0
                ? 'Finché ci sono trattative dentro non si può eliminare: al massimo si spegne.'
                : 'Non ci sono trattative dentro: si può eliminare.'}
            </span>
            <button className="btn btn-sm" onClick={onDelete}>
              <Icon name="trash" size={12} /> Elimina questa pipeline
            </button>
          </div>
        </>
      )}
    </>
  );
}

// ════════════════════════════════════════════════════════════════════
// Gli step: nome, quanto ci si crede, cosa vuol dire arrivarci
// ════════════════════════════════════════════════════════════════════

function StepDellaPipeline({ p, canWrite }: { p: PipelineConfig; canWrite: boolean }) {
  const { addStage, moveStage } = usePipelineActions();
  const [nuovo, setNuovo] = useState(false);

  const mancano = mancanzeStep(p);
  // Nelle pipeline del bando il codice guarda un nome solo, e solo per impedire
  // di dire "requisiti verificati" quando non lo sono: se lo step sparisce o
  // cambia nome, il controllo smette di scattare in silenzio. Meglio dirlo.
  const guardiaVoucher =
    p.kind === PipelineKind.VOUCHER && !p.stages.some((s) => s.name === VOUCHER_REQUISITI_STAGE);

  return (
    <>
      <div className="ed-divider" />

      <div className="data-hd">
        <span className="tk-sez-t">Le fasi, in ordine</span>
        {canWrite && !nuovo && (
          <button className="btn btn-sm" onClick={() => setNuovo(true)}>
            <Icon name="plus" size={12} /> Aggiungi passaggio
          </button>
        )}
      </div>

      {!!mancano.length && (
        <div className="adm-note">
          <Icon name="alert" size={12} className="mute" />
          <span>
            A questa pipeline manca {mancano.join(' e ')}. Si può lasciare così, ma senza uno step
            "vinta" non si riesce a chiudere una trattativa portandola a casa.
          </span>
        </div>
      )}

      {guardiaVoucher && (
        <div className="adm-note">
          <Icon name="alert" size={12} className="mute" />
          <span>
            In una pipeline del bando lo step chiamato esattamente <strong>{VOUCHER_REQUISITI_STAGE}</strong> è
            quello che impedisce di andare avanti coi requisiti ancora da sistemare. Qui non c'è: il controllo
            non scatterà.
          </span>
        </div>
      )}

      {/* Le colonne vanno dette: da sole "70" e "In corso" non significano niente
          per chi apre questa pagina una volta ogni sei mesi. */}
      <div className="pl-step pl-step-hd">
        <span />
        <span className="t-micro">Fase</span>
        <span className="t-micro">Ci si crede %</span>
        <span className="t-micro">Vuol dire</span>
        <span />
      </div>

      <div className="flex flex-col">
        {p.stages.map((s, i) => (
          <RigaStep
            key={s.id}
            s={s}
            n={i + 1}
            primo={i === 0}
            ultimo={i === p.stages.length - 1}
            canWrite={canWrite}
            movePending={moveStage.isPending}
            onMove={(direction) => moveStage.mutate({ stageId: s.id, direction })}
          />
        ))}
      </div>

      {nuovo && (
        <NuovoStep
          pipelineId={p.id}
          pending={addStage.isPending}
          onClose={() => setNuovo(false)}
          onAdd={(body) => addStage.mutate({ pipelineId: p.id, ...body }, { onSuccess: () => setNuovo(false) })}
        />
      )}
    </>
  );
}

function RigaStep({ s, n, primo, ultimo, canWrite, movePending, onMove }: {
  s: PipelineStageConfig;
  n: number;
  primo: boolean;
  ultimo: boolean;
  canWrite: boolean;
  movePending: boolean;
  onMove: (d: MoveDirection) => void;
}) {
  const { updateStage, deleteStage } = usePipelineActions();

  if (!canWrite) {
    return (
      <div className="pl-step">
        <span className="pl-step-n">{n}</span>
        <span>{s.name}</span>
        <span className="mono mute text-[12px]">{s.probability}%</span>
        <span className="mute text-[12px]">{STAGE_OUTCOME_LABEL[s.outcome]}</span>
        <span />
      </div>
    );
  }

  return (
    <div className="pl-step">
      <span className="pl-step-n">{n}</span>
      <input
        className="inp tk-inline"
        key={s.name}
        maxLength={MAX_NOME}
        defaultValue={s.name}
        aria-label="Nome del passaggio"
        onBlur={(e) => { const v = e.target.value.trim(); if (v && v !== s.name) updateStage.mutate({ stageId: s.id, name: v }); }}
      />
      <input
        className="inp tk-inline mono"
        type="number"
        min={0}
        max={100}
        step={5}
        key={s.probability}
        defaultValue={s.probability}
        aria-label="Quanto ci si crede, in percentuale"
        title="Quanto ci si crede quando una trattativa è ferma qui"
        onBlur={(e) => {
          // Campo svuotato (o riempito con qualcosa che non è un numero): si
          // rimette il valore di prima. Number('') fa 0, e uscendo dal campo
          // per sbaglio la probabilità dello step sarebbe andata a zero senza
          // che nessuno se ne accorgesse.
          const grezzo = e.target.value.trim();
          const n = Number(grezzo);
          if (!grezzo || !Number.isFinite(n)) { e.target.value = String(s.probability); return; }
          const v = Math.max(0, Math.min(100, Math.round(n)));
          if (v !== s.probability) updateStage.mutate({ stageId: s.id, probability: v });
          else e.target.value = String(s.probability);   // "07" o "100.4" tornano com'erano
        }}
      />
      <select
        className="inp tk-inline"
        value={s.outcome}
        aria-label="Esito"
        onChange={(e) => updateStage.mutate({ stageId: s.id, outcome: e.target.value as StageOutcome })}
      >
        {Object.values(StageOutcome).map((o) => <option key={o} value={o}>{STAGE_OUTCOME_LABEL[o]}</option>)}
      </select>
      <div className="pl-step-side">
        {s.dealsCount > 0 && (
          <span className="mute-2 text-[12px] mr-1 whitespace-nowrap" title="Trattative ferme su questo passaggio">
            {s.dealsCount}
          </span>
        )}
        <Riordino primo={primo} ultimo={ultimo} pending={movePending} cosa="il passaggio" onMove={onMove} />
        <button
          type="button"
          className="pl-ico is-danger m-hit"
          title="Elimina questo passaggio"
          aria-label="Elimina questo passaggio"
          disabled={deleteStage.isPending}
          onClick={() => deleteStage.mutate({ stageId: s.id, name: s.name })}
        >
          <Icon name="trash" size={13} />
        </button>
      </div>
    </div>
  );
}

function NuovoStep({ pipelineId, pending, onClose, onAdd }: {
  pipelineId: string;
  pending: boolean;
  onClose: () => void;
  onAdd: (b: { name: string; probability: number; outcome: StageOutcome }) => void;
}) {
  const [name, setName] = useState('');
  const [probability, setProbability] = useState(50);
  const [outcome, setOutcome] = useState<StageOutcome>(StageOutcome.APERTO);

  const salva = () => {
    const v = name.trim();
    if (v) onAdd({ name: v, probability, outcome });
  };

  return (
    <div className="pl-step mt-2" key={pipelineId}>
      <span className="pl-step-n">+</span>
      <input
        className="inp tk-inline"
        autoFocus
        maxLength={MAX_NOME}
        placeholder="Per esempio: Sopralluogo fatto"
        value={name}
        onChange={(e) => setName(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') salva(); if (e.key === 'Escape') onClose(); }}
      />
      <input
        className="inp tk-inline mono"
        type="number"
        min={0}
        max={100}
        step={5}
        value={probability}
        aria-label="Quanto ci si crede, in percentuale"
        onChange={(e) => setProbability(Number(e.target.value))}
      />
      <select className="inp tk-inline" value={outcome} aria-label="Esito" onChange={(e) => setOutcome(e.target.value as StageOutcome)}>
        {Object.values(StageOutcome).map((o) => <option key={o} value={o}>{STAGE_OUTCOME_LABEL[o]}</option>)}
      </select>
      <div className="pl-step-side">
        <button className="btn btn-primary btn-sm" disabled={!name.trim() || pending} onClick={salva}>Aggiungi</button>
        <button className="btn btn-sm btn-ghost" onClick={onClose}>Annulla</button>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// Pezzi comuni
// ════════════════════════════════════════════════════════════════════

/** Due frecce al posto del trascinamento: si sposta di un posto per volta. */
function Riordino({ primo, ultimo, pending, cosa, onMove }: {
  primo: boolean;
  ultimo: boolean;
  pending: boolean;
  cosa: string;
  onMove: (d: MoveDirection) => void;
}) {
  return (
    <>
      {/* m-hit: solo sul telefono allarga il bersaglio a 44px con un riquadro
          invisibile, senza gonfiare l'icona. Sul computer .m-hit non esiste. */}
      <button
        type="button"
        className="pl-ico m-hit"
        title={`Sposta ${cosa} più in alto`}
        aria-label={`Sposta ${cosa} più in alto`}
        disabled={primo || pending}
        onClick={() => onMove('su')}
      >
        <Icon name="arrowUp" size={13} />
      </button>
      <button
        type="button"
        className="pl-ico m-hit"
        title={`Sposta ${cosa} più in basso`}
        aria-label={`Sposta ${cosa} più in basso`}
        disabled={ultimo || pending}
        onClick={() => onMove('giu')}
      >
        <Icon name="arrowDown" size={13} />
      </button>
    </>
  );
}

function Riga({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <div className="ed-kv-row">
      <div className="k">{label}{hint && <div className="tk-kv-hint">{hint}</div>}</div>
      <div className="v">{children}</div>
    </div>
  );
}
